'use client';

import { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Users, RefreshCw, Zap } from 'lucide-react';

interface WelcomeScreen {
  heading: string;
  icon: typeof Users;
  body: React.ReactNode;
}

const SCREENS: WelcomeScreen[] = [
  {
    heading: 'Your Clients, Always in Sync',
    icon: Users,
    body: (
      <>
        <p className="text-base">
          Every client who texts your agency is automatically added to your contact directory.
          No manual entry, no spreadsheets, no missing records.
        </p>
        <p className="text-[15px]">
          Voley gives you a <strong>single source of truth</strong> for every client
          relationship &mdash; searchable, organized, and always up to date.
        </p>
        <div className="flex flex-col gap-2.5 pl-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]"><strong>Automatic contact creation</strong> when new clients text your agency</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]"><strong>AMS sync</strong> keeps contact details current across both systems</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]"><strong>Tags, notes, and consent</strong> tracked per contact for full visibility</span>
          </div>
        </div>
      </>
    ),
  },
  {
    heading: 'AMS Integration',
    icon: RefreshCw,
    body: (
      <>
        <p className="text-base">
          When your AMS is connected, Voley automatically syncs your client directory:
        </p>
        <div className="flex flex-col gap-2.5 pl-2">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]"><strong>Real-time updates</strong> &mdash; name, phone, address changes in your AMS are reflected instantly</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]"><strong>Two-way matching</strong> &mdash; incoming texts are matched to existing AMS clients automatically</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-5 w-5 rounded-full bg-accent-blue text-white text-[10px] font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]"><strong>No duplicate records</strong> &mdash; smart matching ensures one contact per client</span>
          </div>
        </div>
      </>
    ),
  },
  {
    heading: "Let's Explore Contacts",
    icon: Zap,
    body: (
      <>
        <p className="text-base">
          We&rsquo;ll walk you through:
        </p>
        <div className="flex flex-col gap-3 pl-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">1</div>
            <span className="text-[15px]">Your contact directory &mdash; search, filter, and bulk message</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">2</div>
            <span className="text-[15px]">Contact details panel &mdash; editing, consent, and organization</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-accent-blue text-white text-xs font-bold shrink-0 mt-0.5">3</div>
            <span className="text-[15px]">Conversation management tools &mdash; notes, tags, and more</span>
          </div>
        </div>
      </>
    ),
  },
];

interface ContactManagementWelcomeModalProps {
  onComplete: () => void;
}

export function ContactManagementWelcomeModal({ onComplete }: ContactManagementWelcomeModalProps) {
  const [activeScreen, setActiveScreen] = useState(0);
  const isLastScreen = activeScreen === SCREENS.length - 1;

  const handleNext = useCallback(() => {
    if (isLastScreen) {
      onComplete();
    } else {
      setActiveScreen((prev) => prev + 1);
    }
  }, [isLastScreen, onComplete]);

  const handleBack = useCallback(() => {
    setActiveScreen((prev) => Math.max(0, prev - 1));
  }, []);

  const screen = SCREENS[activeScreen];
  const Icon = screen.icon;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" />

      {/* Modal */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4">
        <div
          className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 fade-in duration-300"
          role="dialog"
          aria-modal="true"
          aria-label="Contact Management Tour Welcome"
        >
          {/* Content */}
          <div className="p-8">
            {/* Icon and Heading Row */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl shrink-0 bg-accent-blue/[0.08]">
                <Icon className="h-6 w-6 text-accent-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-1 text-gray-900">{screen.heading}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="text-left leading-relaxed mb-8 space-y-4 text-gray-600 ml-16">
              {screen.body}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              {activeScreen > 0 ? (
                <button
                  onClick={handleBack}
                  aria-label="Previous screen"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <div />
              )}

              <button
                onClick={handleNext}
                aria-label={isLastScreen ? 'Start the tour' : 'Next screen'}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastScreen ? "Let's Go" : 'Next'}
                {!isLastScreen && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>

            {/* Step dots */}
            <div className="flex justify-center gap-1.5 mt-5">
              {SCREENS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-200 ${
                    i === activeScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
